import { useMemo } from 'react';

import { TFunction } from 'i18next';
import { AlertTriangleIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import {
  aliasLabel,
  prettyProvider,
  providerIcon,
} from '@/modules/aiAgents/components/ModelStrategy/ModelPicker';
import { useModelStrategy } from '@/modules/aiAgents/hooks/useModelStrategy';
import {
  findCatalogEntry,
  useModelStrategyCatalog,
} from '@/modules/aiAgents/hooks/useModelStrategyCatalog';
import { selectAccountSlug } from '@/redux/session/selectors';
import {
  legacyDefaultChain,
  MODEL_STRATEGY_CHANNEL_KEYS,
  ModelCatalogEntry,
  ModelStrategyBucket,
  ModelStrategyStep,
  stepBucket,
  stepSelectorKey,
} from '@/shared/types/modelStrategy';

const BUCKET_LABEL_KEYS: Record<ModelStrategyBucket, string> = {
  internal: 'try_it.model_override.chain.buckets.internal',
  proxy: 'try_it.model_override.chain.buckets.proxy',
  external: 'try_it.model_override.chain.buckets.external',
};

const stepLabel = (
  entry: ModelCatalogEntry | undefined,
  step: ModelStrategyStep,
  t: TFunction
): string => {
  if (entry) {
    return aliasLabel(entry.alias, t);
  }
  if (step.language_model_alias) {
    return aliasLabel(step.language_model_alias, t);
  }
  return t('ai_agents.model_strategy.picker.unknown_byollm');
};

type FallbackChainProps = {
  brainId: string | undefined;
  legacyLanguageModelId: string | null | undefined;
};

export const FallbackChain = ({ brainId, legacyLanguageModelId }: FallbackChainProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { strategy, getStatus, canRead } = useModelStrategy(brainId);
  const { buckets, status: catalogStatus } = useModelStrategyCatalog(brainId);

  const steps = useMemo<ModelStrategyStep[]>(() => {
    const configured = strategy.chat.length > 0 ? strategy.chat : strategy.default;
    if (configured.length > 0) {
      return configured;
    }
    const hasAnyChannel = MODEL_STRATEGY_CHANNEL_KEYS.some(
      (channel) => (strategy[channel]?.length ?? 0) > 0
    );
    return hasAnyChannel ? [] : legacyDefaultChain(legacyLanguageModelId);
  }, [strategy, legacyLanguageModelId]);

  if (!canRead) {
    return null;
  }

  const settingsPath = slug && brainId ? `/${slug}/ai-agents/${brainId}/settings` : null;
  const isLoading = getStatus === 'pending' || catalogStatus === 'pending';
  const hasFailed = getStatus === 'error';

  return (
    <div className="flex w-full max-w-145 flex-col items-start gap-2">
      <div className="flex flex-col items-start justify-center gap-0.5 self-stretch">
        <h3 className="text-sm leading-5 font-medium text-foreground">
          {t('try_it.model_override.chain.title')}
        </h3>
        <p className="self-stretch text-sm leading-5 font-normal text-muted-foreground">
          <Trans
            i18nKey="try_it.model_override.chain.subtitle"
            components={{
              settingsLink: settingsPath ? (
                <Link
                  to={settingsPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2"
                />
              ) : (
                <span />
              ),
            }}
          />
        </p>
      </div>

      {hasFailed && (
        <p role="alert" className="text-sm leading-5 text-warning">
          {t('ai_agents.model_strategy.errors.load_failed')}
        </p>
      )}

      {!isLoading && !hasFailed && (
        <ol className="flex flex-col items-start gap-2 self-stretch">
          {steps.map((step, index) => {
            const entry = findCatalogEntry(buckets, step);
            const bucket = entry?.bucket ?? stepBucket(step);
            const provider = prettyProvider(entry?.provider, t);
            const model = stepLabel(entry, step, t);
            const isUnavailable = catalogStatus === 'success' && !!step.language_model_id && !entry;

            return (
              <li
                key={stepSelectorKey(step)}
                className="flex items-center gap-2 self-stretch rounded-lg border border-border bg-muted px-2.5 py-2"
              >
                <span className="w-4 shrink-0 text-xs leading-4 font-normal text-muted-foreground">
                  {index + 1}
                </span>
                <span className="flex shrink-0 items-center justify-center rounded-md border border-border bg-background p-2">
                  {providerIcon(entry?.provider)}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  {provider ? (
                    <>
                      <span className="truncate text-xs leading-4 font-medium text-foreground">
                        {provider}
                      </span>
                      <span className="truncate text-xs leading-4 font-normal text-muted-foreground">
                        {model}
                      </span>
                    </>
                  ) : (
                    <span className="truncate text-xs leading-4 font-medium text-foreground">
                      {model}
                    </span>
                  )}
                  {isUnavailable && (
                    <span
                      role="alert"
                      className="flex items-center gap-1 text-xs leading-4 text-warning"
                    >
                      <AlertTriangleIcon className="size-3 shrink-0" aria-hidden="true" />
                      {t('ai_agents.model_strategy.row_unavailable')}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'flex shrink-0 items-center justify-center rounded-md border bg-background px-2 py-1 text-xs leading-4 font-semibold',
                    bucket === 'internal'
                      ? 'border-selected text-foreground'
                      : 'border-border text-foreground'
                  )}
                >
                  {t(BUCKET_LABEL_KEYS[bucket])}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
